import { useState } from 'react'
import { Link } from 'react-router-dom'

import { format } from 'date-fns'
import { Target, Plus, Trash2, ChevronRight, Calendar, Briefcase, Search } from 'lucide-react'
import { EmptyState } from '@/components/EmptyState'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  useContactTargets,
  useCreateContactTarget,
  useUpdateContactTarget,
  useDeleteContactTarget,
} from '@/hooks/use-contact-targets'
import { useContacts } from '@/hooks/use-contacts'
import type { ContactTarget, TargetStatus } from '@/lib/types'

const PIPELINE_STAGES: { status: TargetStatus; label: string; color: string }[] = [
  {
    status: 'prospect',
    label: 'Prospect',
    color: 'tag-sand',
  },
  {
    status: 'approaching',
    label: 'Approaching',
    color: 'tag-lilac',
  },
  {
    status: 'introduced',
    label: 'Introduced',
    color: 'tag-coral',
  },
  {
    status: 'connected',
    label: 'Connected',
    color: 'tag-chartreuse',
  },
  {
    status: 'achieved',
    label: 'Achieved',
    color: 'tag-deep-green',
  },
]

function getInitials(firstName: string, lastName?: string | null) {
  return `${firstName.charAt(0)}${lastName ? lastName.charAt(0) : ''}`.toUpperCase()
}

export default function TargetsPage() {
  const { data: targets, isLoading } = useContactTargets()
  const createTarget = useCreateContactTarget()
  const updateTarget = useUpdateContactTarget()
  const deleteTarget = useDeleteContactTarget()

  const [showAdd, setShowAdd] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedContact, setSelectedContact] = useState('')
  const [goal, setGoal] = useState('')
  const [targetDate, setTargetDate] = useState('')

  // Contact search for add form
  const { data: contactsData } = useContacts({ q: searchQuery, limit: 10 })
  const searchResults = contactsData?.items ?? []

  const grouped = PIPELINE_STAGES.map((stage) => ({
    ...stage,
    targets: (targets ?? []).filter((t) => t.status === stage.status),
  }))

  function handleAdd() {
    if (!selectedContact) return
    createTarget.mutate(
      {
        contact_id: selectedContact,
        goal: goal || undefined,
        target_date: targetDate || undefined,
      },
      {
        onSuccess: () => {
          setShowAdd(false)
          setSelectedContact('')
          setGoal('')
          setTargetDate('')
          setSearchQuery('')
        },
      },
    )
  }

  function handleStatusChange(target: ContactTarget, newStatus: TargetStatus) {
    updateTarget.mutate({ id: target.id, status: newStatus })
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Targets</h1>
          <p className="text-muted-foreground text-sm">
            People you want to build relationships with
          </p>
        </div>
        <Button onClick={() => setShowAdd(true)} size="sm">
          <Plus className="mr-1 h-4 w-4" />
          Add Target
        </Button>
      </div>

      {/* Add target form */}
      {showAdd && (
        <div className="bg-card mb-6 rounded-xl border p-4 shadow-sm">
          <h3 className="mb-3 font-semibold">Add a new target</h3>
          <div className="space-y-3">
            <div>
              <label className="text-muted-foreground mb-1 block text-sm">Search contact</label>
              <div className="relative">
                <Search className="text-muted-foreground absolute top-2.5 left-3 h-4 w-4" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setSelectedContact('')
                  }}
                  placeholder="Type a name..."
                  className="border-input bg-background w-full rounded-lg border py-2 pr-3 pl-9 text-sm"
                />
              </div>
              {searchQuery && !selectedContact && searchResults.length > 0 && (
                <div className="bg-card mt-1 max-h-40 overflow-y-auto rounded-lg border shadow-lg">
                  {searchResults.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => {
                        setSelectedContact(c.id)
                        setSearchQuery(`${c.firstName} ${c.lastName ?? ''}`.trim())
                      }}
                      className="hover:bg-muted flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                    >
                      <div className="bg-primary/10 text-primary flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold">
                        {getInitials(c.firstName, c.lastName)}
                      </div>
                      <div>
                        <div className="font-medium">
                          {c.firstName} {c.lastName}
                        </div>
                        {c.company && (
                          <div className="text-muted-foreground text-xs">{c.company}</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="text-muted-foreground mb-1 block text-sm">
                Goal (why do you want to connect?)
              </label>
              <input
                type="text"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder="e.g., Potential mentor, business partnership..."
                className="border-input bg-background w-full rounded-lg border px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-muted-foreground mb-1 block text-sm">
                Target date (optional)
              </label>
              <input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="border-input bg-background w-full rounded-lg border px-3 py-2 text-sm"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleAdd}
                disabled={!selectedContact || createTarget.isPending}
                size="sm"
              >
                {createTarget.isPending ? 'Adding...' : 'Add Target'}
              </Button>
              <Button variant="outline" onClick={() => setShowAdd(false)} size="sm">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
        </div>
      ) : !targets?.length && !showAdd ? (
        <EmptyState
          icon={Target}
          title="No targets yet"
          description="Add contacts you want to build relationships with and track your progress."
        />
      ) : (
        <div className="space-y-4">
          {grouped.map((stage) => (
            <div key={stage.status}>
              <div className="mb-2 flex items-center gap-2">
                <span
                  className={cn('rounded-full px-2.5 py-0.5 text-xs font-semibold', stage.color)}
                >
                  {stage.label}
                </span>
                <span className="text-muted-foreground text-xs">{stage.targets.length}</span>
              </div>
              {stage.targets.length === 0 ? (
                <div className="text-muted-foreground rounded-lg border border-dashed py-3 text-center text-xs">
                  No targets in this stage
                </div>
              ) : (
                <div className="space-y-2">
                  {stage.targets.map((target) => (
                    <TargetCard
                      key={target.id}
                      target={target}
                      stages={PIPELINE_STAGES}
                      onStatusChange={handleStatusChange}
                      onDelete={() => deleteTarget.mutate(target.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function TargetCard({
  target,
  stages,
  onStatusChange,
  onDelete,
}: {
  target: ContactTarget
  stages: typeof PIPELINE_STAGES
  onStatusChange: (target: ContactTarget, status: TargetStatus) => void
  onDelete: () => void
}) {
  const [expanded, setExpanded] = useState(false)
  const name = `${target.contactFirstName ?? ''} ${target.contactLastName ?? ''}`.trim()

  return (
    <div className="bg-card rounded-xl border p-3 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-center gap-3">
        <Link to={`/contacts/${target.contactId}`}>
          <div className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold">
            {target.contactAvatarUrl ? (
              <img
                src={target.contactAvatarUrl}
                alt={name}
                className="h-10 w-10 rounded-full object-cover"
              />
            ) : (
              getInitials(target.contactFirstName ?? '', target.contactLastName)
            )}
          </div>
        </Link>
        <div className="min-w-0 flex-1">
          <Link to={`/contacts/${target.contactId}`} className="font-semibold hover:underline">
            {name}
          </Link>
          {(target.contactCompany || target.contactJobTitle) && (
            <div className="text-muted-foreground flex items-center gap-1 text-xs">
              <Briefcase className="h-3 w-3" />
              {[target.contactJobTitle, target.contactCompany].filter(Boolean).join(' at ')}
            </div>
          )}
          {target.goal && <div className="text-muted-foreground mt-0.5 text-xs">{target.goal}</div>}
        </div>
        <div className="flex items-center gap-1">
          {target.targetDate && (
            <span className="text-muted-foreground flex items-center gap-1 text-xs">
              <Calendar className="h-3 w-3" />
              {format(new Date(target.targetDate), 'MMM d')}
            </span>
          )}
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-muted-foreground hover:text-foreground rounded p-1"
          >
            <ChevronRight className={cn('h-4 w-4 transition-transform', expanded && 'rotate-90')} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 border-t pt-3">
          {target.strategyNotes && (
            <p className="text-muted-foreground mb-3 text-sm">{target.strategyNotes}</p>
          )}
          <div className="mb-3 flex flex-wrap gap-1">
            {stages.map((stage) => (
              <button
                key={stage.status}
                onClick={() => onStatusChange(target, stage.status)}
                className={cn(
                  'rounded-full px-2.5 py-1 text-xs font-medium transition-all',
                  target.status === stage.status
                    ? stage.color
                    : 'bg-muted text-muted-foreground hover:bg-muted/80',
                )}
              >
                {stage.label}
              </button>
            ))}
          </div>
          <div className="flex justify-end">
            <button
              onClick={onDelete}
              className="text-muted-foreground hover:text-destructive flex items-center gap-1 text-xs"
            >
              <Trash2 className="h-3 w-3" />
              Remove
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
